import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  MoreHorizontal, 
  Calendar,
  Mail,
  Lock,
  AlertCircle,
  User as UserIcon,
} from "lucide-react";
import { User as UserType } from "@/lib/schema";
import { getRoleColor, getRoleIcon, getInitials } from "@/lib/userUtils";

interface UserCardProps {
  user: UserType;
  currentUser: UserType | null;
  onUserSelect: (user: UserType) => void;
}

export const UserCard = ({ user, currentUser, onUserSelect }: UserCardProps) => {
  const RoleIcon = getRoleIcon(user.role);
  const isOwnCard = currentUser?.id === user.id;

  return (
    <Card className={`hover:shadow-md transition-shadow ${isOwnCard ? 'relative' : ''}`}>
      {isOwnCard && (
        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm rounded-lg z-10 flex items-center justify-center">
          <div className="text-center text-white p-4">
            <Lock className="h-8 w-8 mx-auto mb-2" />
            <p className="font-semibold">Your Account</p>
            <p className="text-sm opacity-90">You cannot edit your own access</p>
          </div>
        </div>
      )}
      
      <CardContent className={`p-6 ${isOwnCard ? 'opacity-75' : ''}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={user.profileImageUrl || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-blue-400 to-blue-600 text-white">
                {getInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-sm text-gray-600">{user.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onUserSelect(user)}
            data-testid={`user-menu-${user.id}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RoleIcon className="h-4 w-4 text-gray-500" />
              <Badge className={getRoleColor(user.role)}>
                {user.role.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            {isOwnCard && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-4 w-4" />
                <span className="text-xs">Your account</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Mail className="h-4 w-4" />
            <span>{user.email}</span>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>
              Joined {new Date(user.createdAt).toLocaleDateString()}
            </span>
          </div>

          {user.id.startsWith('temp_') && (
            <div className="flex items-center gap-2 text-sm text-purple-600">
              <Mail className="h-4 w-4" />
              <span>Invitation pending</span>
            </div>
          )}
          
          {!user.id.startsWith('temp_') && user.passwordResetRequired === true && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <AlertCircle className="h-4 w-4" />
              <span>Password reset required</span>
            </div>
          )}
          
          {!user.id.startsWith('temp_') && user.onboardingCompleted === false && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <UserIcon className="h-4 w-4" />
              <span>Onboarding pending</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
